import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Check,
  ArrowRight,
  MessageCircle,
  Loader2,
  FileText,
  ShieldCheck,
  Ticket,
  Send,
  Stamp,
  RefreshCcw,
  Lock,
  Plane,
  Hotel,
  Sparkles,
} from "lucide-react";
import AirportPicker from "../components/AirportPicker";
import Navbar from "../components/Navbar";
import AirlineMarquee from "../components/AirlineMarquee";
import VisaUseCases from "../components/VisaUseCases";
import Footer from "../components/Footer";
import StickyCTA from "../components/StickyCTA";
import useSEO from "../hooks/useSEO";
import { track } from "../lib/analytics";
import {
  getEnabledProducts,
  getProductById,
  formatPrice,
} from "../config/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CANONICAL_HOME = "https://getdeparto.com";
const OG_IMAGE = `${CANONICAL_HOME}/og-image.png`;

const HOMEPAGE_SEO = {
  title: "Verifiable Flight Reservations for Visa Applications | GetDeparto",
  description:
    "Get a temporary flight reservation with a live PNR, delivered to WhatsApp and email within 60 minutes. Used for Schengen, UK, Japan and other visa applications.",
  canonical: CANONICAL_HOME,
  og: {
    title: "Verifiable Flight Reservations for Visa Applications | GetDeparto",
    description:
      "Get a temporary flight reservation with a live PNR, delivered to WhatsApp and email within 60 minutes. Used for Schengen, UK, Japan and other visa applications.",
    url: CANONICAL_HOME,
    image: OG_IMAGE,
    type: "website",
    siteName: "GetDeparto",
  },
  twitter: {
    card: "summary_large_image",
    title: "Verifiable Flight Reservations for Visa Applications | GetDeparto",
    description:
      "Get a temporary flight reservation with a live PNR, delivered to WhatsApp and email within 60 minutes.",
    image: OG_IMAGE,
  },
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "GetDeparto — Flight Reservation for Visa Applications",
      serviceType: "Temporary flight reservation with verifiable PNR",
      description:
        "Temporary flight reservation with a live PNR, suitable for visa documentation and proof of onward travel. Delivered via WhatsApp and email within 60 minutes.",
      url: CANONICAL_HOME,
      image: OG_IMAGE,
      provider: {
        "@type": "Organization",
        name: "Get Departo",
        url: CANONICAL_HOME,
        logo: OG_IMAGE,
        areaServed: "Worldwide",
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          availableLanguage: ["English", "Hindi"],
          email: "support@getdeparto.com",
        },
      },
      areaServed: "Worldwide",
      audience: {
        "@type": "Audience",
        audienceType: "Visa applicants and international travellers",
      },
      offers: [
        {
          "@type": "Offer",
          name: "One-way Flight Reservation",
          price: "599",
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          url: CANONICAL_HOME,
        },
        {
          "@type": "Offer",
          name: "Return Flight Reservation",
          price: "899",
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          url: CANONICAL_HOME,
        },
        {
          "@type": "Offer",
          name: "Hotel Reservation",
          price: "299",
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          url: CANONICAL_HOME,
        },
        {
          "@type": "Offer",
          name: "Full Visa Pack",
          price: "1549",
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          url: CANONICAL_HOME,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "GetDeparto",
      legalName: "Get Departo",
      url: CANONICAL_HOME,
      logo: OG_IMAGE,
      sameAs: [],
    },
  ],
};

const PRODUCT_ICONS = {
  one_way_flight: Plane,
  return_flight: Plane,
  hotel_single: Hotel,
  full_visa_pack: Sparkles,
};

const FAQS = [
  {
    q: "Is this a real flight ticket?",
    a: "Yes, this is a temporary flight reservation with a verifiable PNR.",
  },
  {
    q: "Can I verify the PNR?",
    a: "Yes, you can check the PNR on the airline website during its validity period.",
  },
  {
    q: "Is this valid for visa applications?",
    a: "Yes, many visa applications require a travel itinerary or reservation. This service provides that.",
  },
  {
    q: "How long is it valid?",
    a: "Each product has its own validity. The one-way flight reservation is valid for 5 days. The return flight reservation, hotel reservation, and Full Visa Pack are each valid for 10 days.",
  },
  {
    q: "What if the PNR does not work?",
    a: "It will. But if it doesn't, we will resolve the issue within 60 minutes and reissue or refund your money.",
  },
  {
    q: "Does this guarantee visa approval?",
    a: "No, visa decisions are made by embassies or consulates. This helps in securing a valid flight ticket for visa documentation.",
  },
];

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="gd-faq-item" data-testid={`faq-item-${idx}`}>
      <button
        className="gd-faq-trigger"
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-trigger-${idx}`}
      >
        <span>{q}</span>
        <span
          style={{
            color: "#6B7280",
            fontSize: 20,
            transform: open ? "rotate(45deg)" : "none",
            transition: "transform .15s",
          }}
        >
          +
        </span>
      </button>
      {open && (
        <div className="gd-faq-content" data-testid={`faq-content-${idx}`}>
          {a}
        </div>
      )}
    </div>
  );
}

function HowCard({ step, title, body, testid }) {
  return (
    <div className="gd-card" style={{ padding: 24 }} data-testid={testid}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "var(--primary-soft)",
          color: "var(--primary)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 14,
        }}
      >
        {step}
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, marginBottom: 8 }}>{title}</h3>
      <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0, lineHeight: 1.55 }}>
        {body}
      </p>
    </div>
  );
}

function TrustCard({ icon: Icon, number, title, body, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: 24,
        boxShadow: "0 1px 2px rgba(17,24,39,0.04), 0 8px 24px -16px rgba(17,24,39,0.08)",
        height: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div
          style={{
            width: 40, height: 40, borderRadius: 10,
            background: "var(--primary-soft)", color: "var(--primary)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon size={18} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-subtle)", letterSpacing: "0.12em" }}>
          {number}
        </span>
      </div>
      <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0, marginBottom: 8, letterSpacing: "-0.01em" }}>{title}</h3>
      <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}

function SampleTicket() {
  return (
    <div
      className="gd-card"
      data-testid="sample-ticket"
      style={{ padding: 0, maxWidth: 760, margin: "0 auto", overflow: "hidden", position: "relative", background: "#fff" }}
    >
      <div
        style={{
          padding: "16px 22px",
          background: "var(--surface)",
          borderBottom: "1px solid var(--border)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <FileText size={16} color="var(--primary)" />
          <span className="gd-small-caps">Sample flight reservation</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="gd-pill" style={{ fontSize: 11 }}>PDF preview</span>
          <a
            href="/sample/booking-itinerary.pdf"
            target="_blank"
            rel="noreferrer"
            className="gd-btn-ghost"
            style={{ padding: "6px 12px", fontSize: 12 }}
            data-testid="sample-ticket-download"
          >
            <FileText size={13} />
            View / download PDF
          </a>
        </div>
      </div>
      <div style={{ position: "relative", maxHeight: 560, overflow: "hidden",
        background: "linear-gradient(180deg,#f9fafb 0%,#fff 40%,#fff 60%,#f9fafb 100%)" }}
      >
        <img
          src="/sample/booking-itinerary-preview.jpg"
          alt="Sample flight reservation PDF preview"
          style={{ display: "block", width: "100%", height: "auto", objectFit: "cover", objectPosition: "top center" }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute", left: 0, right: 0, bottom: 0, height: 96,
            background: "linear-gradient(180deg,rgba(255,255,255,0) 0%,#fff 90%)",
            pointerEvents: "none",
          }}
        />
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--border)", fontSize: 12,
        color: "var(--text-muted)", textAlign: "center", background: "var(--surface)" }}>
        Sample only · your actual reservation arrives as a PDF with a verifiable PNR
      </div>
    </div>
  );
}

export default function BookingPage() {
  useSEO(HOMEPAGE_SEO);
  const today = new Date().toISOString().split("T")[0];
  const enabledProducts = useMemo(() => getEnabledProducts(), []);
  const defaultProductId =
    enabledProducts.find((p) => p.badge === "Most popular")?.id || enabledProducts[0].id;

  const [selectedProductId, setSelectedProductId] = useState(defaultProductId);
  const selectedProduct = getProductById(selectedProductId);

  const [form, setForm] = useState({
    flight_origin: "DEL",
    flight_destination: "LHR",
    departure_date: "",
    return_date: "",
    hotel_city: "",
    hotel_cities: "",
    check_in_date: "",
    check_out_date: "",
    full_name: "",
    email: "",
    whatsapp: "",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState("");
  const formStartedRef = useRef(false);

  // booking_form_started — fire once when user first interacts with any form input
  const markFormStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    track("booking_form_started", {
      product_id: selectedProductId,
      product_price: selectedProduct.price,
    });
  };

  // Hide sticky CTA during checkout/confirmation
  useEffect(() => {
    const cls = "gd-hide-cta";
    if (submitting || confirmation) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [submitting, confirmation]);

  // Expose currently selected product label so the sticky CTA can read it
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.dataset.gdProduct = selectedProduct.title || "";
    document.body.dataset.gdPrice = formatPrice(selectedProduct.price);
  }, [selectedProduct]);

  const setField = (k, v) => {
    markFormStarted();
    setForm((f) => ({ ...f, [k]: v }));
  };

  const formValid = useMemo(() => {
    const customerOK =
      form.full_name.trim().length > 1 &&
      /^\S+@\S+\.\S+$/.test(form.email) &&
      form.whatsapp.replace(/\D/g, "").length >= 10;
    if (!customerOK) return false;

    if (selectedProduct.requiresFlight) {
      if (!form.flight_origin || !form.flight_destination) return false;
      if (form.flight_origin === form.flight_destination) return false;
      if (!form.departure_date) return false;
      if (selectedProduct.requiresReturnDate) {
        if (!form.return_date) return false;
        if (new Date(form.return_date) <= new Date(form.departure_date)) return false;
      }
    }

    if (selectedProduct.requiresHotel) {
      if (selectedProduct.requiresMultiCityHotels) {
        if (!form.hotel_cities.trim()) return false;
      } else {
        if (!form.hotel_city.trim()) return false;
      }
      if (!form.check_in_date) return false;
      if (form.check_out_date && new Date(form.check_out_date) <= new Date(form.check_in_date)) return false;
    }
    return true;
  }, [form, selectedProduct]);

  const canSubmit = formValid && consent;

  const submit = async () => {
    setError("");
    track("payment_clicked", {
      product_id: selectedProduct.id,
      product_title: selectedProduct.title,
      product_price: selectedProduct.price,
      form_valid: formValid,
      consent_given: consent,
    });
    if (!formValid) {
      if (
        selectedProduct.requiresReturnDate &&
        form.departure_date &&
        form.return_date &&
        new Date(form.return_date) <= new Date(form.departure_date)
      ) {
        setError("Return date must be after the departure date.");
      } else {
        setError("Please complete all required fields.");
      }
      return;
    }
    if (!consent) {
      setError("Please confirm the disclosure checkbox before continuing.");
      return;
    }
    setSubmitting(true);
    try {
      await new Promise((r) => setTimeout(r, 900));
      const payload = {
        selected_product_id: selectedProduct.id,
        product_snapshot: selectedProduct,
        amount: selectedProduct.price,
        full_name: form.full_name,
        email: form.email,
        whatsapp: form.whatsapp,
      };
      if (selectedProduct.requiresFlight) {
        payload.flight_origin = form.flight_origin;
        payload.flight_destination = form.flight_destination;
        payload.departure_date = form.departure_date;
        if (selectedProduct.requiresReturnDate) payload.return_date = form.return_date;
      }
      if (selectedProduct.requiresHotel) {
        if (selectedProduct.requiresMultiCityHotels) {
          payload.hotel_cities = form.hotel_cities;
        } else {
          payload.hotel_city = form.hotel_city;
        }
        payload.check_in_date = form.check_in_date;
        payload.check_out_date = form.check_out_date;
      }
      const { data } = await axios.post(`${API}/orders`, payload);
      track("order_confirmed", {
        order_code: data.order_code,
        product_id: data.selected_product_id,
        product_title: selectedProduct.title,
        amount: data.amount,
        currency: data.currency,
        validity_days: data.validity_days,
      });
      setConfirmation(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not place order. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Confirmation screen ----------
  if (confirmation) {
    const snap = confirmation.product_snapshot || {};
    return (
      <div style={{ minHeight: "100vh", background: "#fff" }}>
        <Navbar />
        <main className="gd-page" style={{ padding: "80px 24px", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 56, height: 56, borderRadius: 99,
              background: "var(--success-soft)", marginBottom: 24,
            }}
          >
            <Check size={28} color="#166534" />
          </div>
          <h1
            className="gd-hero-title"
            style={{ fontSize: 44, fontWeight: 700, letterSpacing: "-0.03em", margin: 0, marginBottom: 14 }}
            data-testid="confirmation-title"
          >
            Your reservation is confirmed
          </h1>
          <p style={{ color: "var(--text-muted)", fontSize: 17, maxWidth: 560, margin: "0 auto 32px" }}>
            You'll receive it on WhatsApp and email within 60 minutes.
          </p>

          <div className="gd-card" style={{ maxWidth: 540, margin: "0 auto", padding: 24, textAlign: "left" }}>
            <div className="gd-small-caps" style={{ marginBottom: 12 }}>Order details</div>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 16px", fontSize: 14 }}>
              <span style={{ color: "#6B7280" }}>Order ID</span>
              <strong data-testid="confirmation-order-code">{confirmation.order_code}</strong>

              <span style={{ color: "#6B7280" }}>Product</span>
              <span data-testid="confirmation-product">{snap.title || confirmation.selected_product_id}</span>

              {snap.includes && (
                <>
                  <span style={{ color: "#6B7280" }}>Includes</span>
                  <span>{snap.includes}</span>
                </>
              )}

              {confirmation.flight_origin && confirmation.flight_destination && (
                <>
                  <span style={{ color: "#6B7280" }}>Route</span>
                  <span>{confirmation.flight_origin} → {confirmation.flight_destination}</span>
                </>
              )}
              {confirmation.departure_date && (
                <>
                  <span style={{ color: "#6B7280" }}>Departure</span>
                  <span>{confirmation.departure_date}</span>
                </>
              )}
              {confirmation.return_date && (
                <>
                  <span style={{ color: "#6B7280" }}>Return</span>
                  <span>{confirmation.return_date}</span>
                </>
              )}
              {confirmation.hotel_city && (
                <>
                  <span style={{ color: "#6B7280" }}>Hotel city</span>
                  <span>{confirmation.hotel_city}</span>
                </>
              )}
              {confirmation.hotel_cities && (
                <>
                  <span style={{ color: "#6B7280" }}>Hotel cities</span>
                  <span>{confirmation.hotel_cities}</span>
                </>
              )}
              {confirmation.check_in_date && (
                <>
                  <span style={{ color: "#6B7280" }}>Check-in</span>
                  <span>{confirmation.check_in_date}</span>
                </>
              )}
              {confirmation.check_out_date && (
                <>
                  <span style={{ color: "#6B7280" }}>Check-out</span>
                  <span>{confirmation.check_out_date}</span>
                </>
              )}

              <span style={{ color: "#6B7280" }}>Customer</span>
              <span>{confirmation.full_name}</span>

              <span style={{ color: "#6B7280" }}>Validity</span>
              <span>{confirmation.validity_days}-day validity</span>

              <span style={{ color: "#6B7280" }}>Delivery</span>
              <span>WhatsApp + email</span>

              <span style={{ color: "#6B7280" }}>Paid</span>
              <span>{formatPrice(confirmation.amount)}</span>
            </div>

            <div
              data-testid="confirmation-summary-line"
              style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border)",
                fontSize: 13, color: "var(--text-muted)" }}
            >
              {snap.summaryLabel} · {confirmation.validity_days}-day validity · {formatPrice(confirmation.amount)}
            </div>
          </div>

          <button
            className="gd-btn-ghost"
            style={{ marginTop: 24 }}
            onClick={() => {
              setConfirmation(null);
              setConsent(false);
              setSelectedProductId(defaultProductId);
              setForm({
                flight_origin: "DEL", flight_destination: "LHR", departure_date: "", return_date: "",
                hotel_city: "", hotel_cities: "", check_in_date: "", check_out_date: "",
                full_name: "", email: "", whatsapp: "",
              });
            }}
            data-testid="confirmation-new-booking-btn"
          >
            Place another booking
          </button>
        </main>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      <Navbar />

      {/* Hero + Booking grid */}
      <section className="gd-hero-section">
        <div className="gd-hero-grid gd-fade-up">
          <div className="gd-hero-left">
            {/* Headline */}
            <div className="gd-hero-headline">
              <span className="gd-badge-blue" data-testid="hero-badge">Verifiable flight reservations</span>
              <h1 className="gd-hero-title" data-testid="hero-title">
                Get a verifiable flight reservation for your visa application
              </h1>
              <p className="gd-hero-sub">
                Temporary flight reservation with a live PNR — delivered to your WhatsApp within
                60 minutes. Suitable for visa documentation and travel planning.
              </p>
            </div>

            {/* Value props (mobile order is handled by CSS) */}
            <ul className="gd-hero-bullets" data-testid="reassurance-bar">
              {[
                { title: "Live PNR during validity", testid: "reassure-pnr" },
                { title: "WhatsApp + email delivery", testid: "reassure-delivery" },
                { title: "Human WhatsApp support", testid: "reassure-support" },
                { title: "Secure Razorpay checkout", testid: "reassure-checkout" },
              ].map(({ title, testid }) => (
                <li className="gd-hero-bullet" data-testid={testid} key={testid}>
                  <span className="gd-hero-bullet-check" aria-hidden>
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span>{title}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Booking card */}
          <div className="gd-hero-card">
            <div className="gd-card" style={{ padding: "22px" }}>
            {/* ========== Choose your document ========== */}
            <div className="gd-small-caps" style={{ marginBottom: 14 }} data-testid="product-section-title">
              Choose your document
            </div>

            <div className="gd-product-grid" data-testid="product-grid">
              {enabledProducts.map((p) => {
                const Icon = PRODUCT_ICONS[p.id] || Plane;
                const selected = selectedProductId === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    className={`gd-product ${selected ? "selected" : ""} ${p.badge ? "badged" : ""}`}
                    onClick={() => {
                      setSelectedProductId(p.id);
                      track("product_selected", {
                        product_id: p.id,
                        product_title: p.title,
                        product_price: p.price,
                      });
                    }}
                    data-testid={`product-card-${p.id}`}
                    aria-pressed={selected}
                  >
                    {p.badge && (
                      <span className="gd-product-badge" data-testid={`product-badge-${p.id}`}>
                        {p.badge}
                      </span>
                    )}
                    {selected && (
                      <span className="gd-product-check" aria-hidden data-testid={`product-check-${p.id}`}>
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                    <div className="gd-product-head">
                      <div className="gd-product-icon"><Icon size={18} /></div>
                      <div className="gd-product-price">{formatPrice(p.price)}</div>
                    </div>
                    <h3 className="gd-product-title">{p.title}</h3>
                    <div className="gd-product-validity">{p.validityDays}-day validity</div>
                    {p.includes && <div className="gd-product-includes">Includes: {p.includes}</div>}
                    <div className="gd-product-bestfor">{p.bestFor}</div>
                    {selected && (
                      <span className="gd-product-selected-line" data-testid={`product-selected-${p.id}`}>
                        Selected <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="gd-form-divider" />

            {/* ========== Flight fields ========== */}
            {selectedProduct.requiresFlight && (
              <>
                <div className="gd-small-caps" style={{ marginBottom: 14 }}>Flight details</div>
                <div className="gd-grid-2"
                  style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <AirportPicker
                    label="From"
                    value={form.flight_origin}
                    onChange={(v) => setField("flight_origin", v)}
                    testid="from-airport"
                  />
                  <AirportPicker
                    label="To"
                    value={form.flight_destination}
                    onChange={(v) => setField("flight_destination", v)}
                    testid="to-airport"
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: selectedProduct.requiresReturnDate ? "1fr 1fr" : "1fr",
                    gap: 16, marginBottom: 16,
                  }}
                >
                  <div>
                    <label className="gd-label">Departure date</label>
                    <input
                      type="date"
                      min={today}
                      className="gd-input"
                      value={form.departure_date}
                      onChange={(e) => {
                        const v = e.target.value;
                        markFormStarted();
                        setForm((f) => ({
                          ...f,
                          departure_date: v,
                          return_date: f.return_date && new Date(f.return_date) <= new Date(v) ? "" : f.return_date,
                        }));
                      }}
                      data-testid="input-departure-date"
                    />
                  </div>
                  {selectedProduct.requiresReturnDate && (
                    <div>
                      <label className="gd-label">Return date</label>
                      <input
                        type="date"
                        min={
                          form.departure_date
                            ? new Date(new Date(form.departure_date).getTime() + 86400000)
                                .toISOString().split("T")[0]
                            : today
                        }
                        className="gd-input"
                        value={form.return_date}
                        onChange={(e) => setField("return_date", e.target.value)}
                        data-testid="input-return-date"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ========== Hotel fields ========== */}
            {selectedProduct.requiresHotel && (
              <>
                <div className="gd-small-caps" style={{ marginBottom: 14, marginTop: selectedProduct.requiresFlight ? 8 : 0 }}>
                  Hotel details
                </div>
                <div style={{ marginBottom: 16 }}>
                  {selectedProduct.requiresMultiCityHotels ? (
                    <>
                      <label className="gd-label">Hotel cities (2–3, comma-separated)</label>
                      <input
                        className="gd-input"
                        placeholder="Paris, Amsterdam, Berlin"
                        value={form.hotel_cities}
                        onChange={(e) => setField("hotel_cities", e.target.value)}
                        data-testid="input-hotel-cities"
                      />
                    </>
                  ) : (
                    <>
                      <label className="gd-label">Hotel city</label>
                      <input
                        className="gd-input"
                        placeholder="Paris, Tokyo, Dubai…"
                        value={form.hotel_city}
                        onChange={(e) => setField("hotel_city", e.target.value)}
                        data-testid="input-hotel-city"
                      />
                    </>
                  )}
                </div>

                <div className="gd-grid-2"
                  style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div>
                    <label className="gd-label">Check-in</label>
                    <input
                      type="date"
                      min={today}
                      className="gd-input"
                      value={form.check_in_date}
                      onChange={(e) => {
                        const v = e.target.value;
                        markFormStarted();
                        setForm((f) => ({
                          ...f,
                          check_in_date: v,
                          check_out_date: f.check_out_date && new Date(f.check_out_date) <= new Date(v) ? "" : f.check_out_date,
                        }));
                      }}
                      data-testid="input-check-in"
                    />
                  </div>
                  <div>
                    <label className="gd-label">Check-out</label>
                    <input
                      type="date"
                      min={
                        form.check_in_date
                          ? new Date(new Date(form.check_in_date).getTime() + 86400000)
                              .toISOString().split("T")[0]
                          : today
                      }
                      className="gd-input"
                      value={form.check_out_date}
                      onChange={(e) => setField("check_out_date", e.target.value)}
                      data-testid="input-check-out"
                    />
                  </div>
                </div>
              </>
            )}

            {/* ========== Customer ========== */}
            <div className="gd-small-caps" style={{ marginBottom: 14, marginTop: 8 }}>
              Your details
            </div>
            <div className="gd-grid-2"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label className="gd-label">Full name as per passport</label>
                <input
                  className="gd-input"
                  placeholder="Rahul Sharma"
                  value={form.full_name}
                  onChange={(e) => setField("full_name", e.target.value)}
                  data-testid="input-full-name"
                />
              </div>
              <div>
                <label className="gd-label">Email</label>
                <input
                  type="email"
                  className="gd-input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  data-testid="input-email"
                />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label className="gd-label">WhatsApp number</label>
              <div style={{ display: "flex", gap: 8 }}>
                <div
                  style={{
                    padding: "12px 14px", background: "var(--surface)",
                    border: "1px solid var(--border)", borderRadius: 8, fontSize: 14,
                    color: "var(--text)", fontWeight: 500, whiteSpace: "nowrap",
                  }}
                >
                  +91
                </div>
                <input
                  className="gd-input"
                  placeholder="98765 43210"
                  value={form.whatsapp}
                  onChange={(e) => setField("whatsapp", e.target.value)}
                  data-testid="input-whatsapp"
                />
              </div>
            </div>

            {/* ========== Order summary ========== */}
            <div className="gd-order-summary" data-testid="order-summary">
              <div className="gd-order-summary-main" data-testid="order-summary-line">
                {selectedProduct.summaryLabel} · {selectedProduct.validityDays}-day validity ·{" "}
                <strong>{formatPrice(selectedProduct.price)}</strong>
              </div>
              {selectedProduct.includes && (
                <div className="gd-order-summary-includes" data-testid="order-summary-includes">
                  Includes: {selectedProduct.includes}
                </div>
              )}
              <div className="gd-order-summary-meta">
                <span><Check size={11} strokeWidth={3} color="var(--success)" /> Delivered via WhatsApp + email</span>
                <span><ShieldCheck size={11} color="var(--success)" /> Reservation verifiable during validity period</span>
                <span><Lock size={11} color="var(--text-muted)" /> Secure payment via Razorpay</span>
              </div>
            </div>

            <p
              data-testid="reissue-refund-note"
              style={{
                marginTop: 12, marginBottom: 18,
                fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.55, textAlign: "center",
              }}
            >
              If your reservation is not verifiable within the promised validity period, we will reissue or refund.
            </p>

            {/* ========== Compliance + Pay ========== */}
            <label
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "14px 16px", border: "1px solid var(--border)", borderRadius: 10,
                background: "var(--surface)", fontSize: 13, color: "var(--text)",
                lineHeight: 1.5, cursor: "pointer", marginBottom: 18,
              }}
              data-testid="compliance-label"
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ marginTop: 3, accentColor: "var(--primary)", width: 16, height: 16, flexShrink: 0 }}
                data-testid="compliance-checkbox"
              />
              <span>
                I understand this is a temporary flight reservation/itinerary and not a paid issued ticket.
            
              </span>
            </label>

            {error && (
              <div
                style={{
                  padding: "10px 14px", background: "var(--danger-soft)", color: "#991b1b",
                  borderRadius: 8, fontSize: 13, marginBottom: 14,
                }}
                data-testid="form-error"
              >
                {error}
              </div>
            )}

            <button
              className="gd-btn-primary"
              onClick={submit}
              disabled={submitting || !canSubmit}
              data-testid="submit-payment-btn"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                  Processing…
                </>
              ) : (
                <>
                  Pay {formatPrice(selectedProduct.price)} securely
                  <ArrowRight size={16} />
                </>
              )}
            </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust cards */}
      <section className="gd-page" style={{ padding: "8px 24px 72px" }}>
        <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 32px" }}>
          <span className="gd-small-caps">Why getdeparto</span>
          <h2 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", margin: "8px 0 10px" }}
            data-testid="trust-cards-title">
            Built for visa documentation, done properly
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: 15, margin: 0, lineHeight: 1.6 }}>
            A small, compliant way to meet onward-travel requirements - without paying for a ticket
            you might not use.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }} className="gd-trust-grid">
          <TrustCard icon={Ticket} number="01" title="Live PNR" body="Your reservation includes a booking reference that can be verified during the selected validity period." testid="trust-card-1" />
          <TrustCard icon={Send} number="02" title="Fast delivery" body="Receive your PDF itinerary via WhatsApp and email within 60 minutes." testid="trust-card-2" />
          <TrustCard icon={Stamp} number="03" title="Visa documentation support" body="Designed for visa applications, travel itineraries, and proof of onward travel." testid="trust-card-3" />
          <TrustCard icon={RefreshCcw} number="04" title="Reissue or refund" body="If your reservation is not verifiable during the validity period, we will reissue or refund." testid="trust-card-4" />
        </div>
      </section>

      <AirlineMarquee />
      <VisaUseCases />

      {/* How it works */}
      <section className="gd-page" style={{ padding: "24px 24px 72px" }}>
        <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.02em", marginBottom: 24 }}
          data-testid="how-it-works-title">
          How it works
        </h2>
        <div className="gd-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <HowCard step="1" title="Choose your document" body="Pick the product that fits your need. The right form fields appear automatically." testid="how-step-1" />
          <HowCard step="2" title="Fill the details" body="Enter your route, dates, and passenger info. Takes under two minutes." testid="how-step-2" />
          <HowCard step="3" title="Receive your itinerary with a live PNR" body="You get the PDF on WhatsApp and email - ready to submit with your visa application." testid="how-step-3" />
        </div>
      </section>

      {/* Sample ticket */}
      <section className="gd-page" style={{ padding: "0 24px 88px" }}>
        <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.02em", marginBottom: 8 }}
          data-testid="sample-ticket-title">
          What you'll receive
        </h2>
        <p style={{ color: "var(--text-muted)", fontSize: 15, margin: "0 0 24px", maxWidth: 620 }}>
          A clean PDF itinerary with a verifiable PNR, passenger name, route, and flight details -
          formatted just like a regular airline booking confirmation.
        </p>
        <SampleTicket />
      </section>

      {/* Concierge support */}
      <section className="gd-page" style={{ padding: "0 24px 64px" }}>
        <div className="gd-concierge" data-testid="concierge-section">
          <span aria-hidden className="gd-concierge-dot gd-concierge-dot-1" />
          <span aria-hidden className="gd-concierge-dot gd-concierge-dot-2" />
          <div className="gd-concierge-inner">
            <div className="gd-concierge-text">
              <span className="gd-concierge-eyebrow">
                <span className="gd-concierge-pulse" aria-hidden />
                Human concierge · online now
              </span>
              <h2 className="gd-concierge-title" data-testid="concierge-title">Questions before ordering?</h2>
              <p className="gd-concierge-sub">
                Talk to a real person on WhatsApp - get help choosing the right product,
                understanding requirements, or placing urgent reservations.
              </p>
              <ul className="gd-concierge-list" data-testid="concierge-features">
                {["Human support", "Fast replies", "Help with visa-related itinerary questions", "Priority processing for urgent requests"].map((it) => (
                  <li key={it}><Check size={14} strokeWidth={3} color="#16a34a" /><span>{it}</span></li>
                ))}
              </ul>
              <div className="gd-concierge-actions">
                <a href="https://wa.me/919036705707" target="_blank" rel="noreferrer"
                  className="gd-concierge-primary" data-testid="concierge-whatsapp-btn">
                  <MessageCircle size={16} /> Chat on WhatsApp
                </a>
                <a href="mailto:support@getdeparto.com" className="gd-concierge-secondary" data-testid="concierge-email-btn">
                  <FileText size={14} /> Email support
                </a>
              </div>
              <div className="gd-concierge-meta">Typical reply time: a few minutes · 24 × 7</div>
            </div>
            <div className="gd-concierge-card" aria-hidden>
              <div className="gd-concierge-avatar"><MessageCircle size={26} color="#fff" /></div>
              <div className="gd-concierge-bubble">
                <span className="gd-concierge-bubble-name">Support · just now</span>
                <span>Hi! Tell us your destination and visa type - we'll suggest the right document in under a minute. 🌍</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="gd-page" style={{ padding: "0 24px 88px", maxWidth: 760 }}>
        <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.02em", marginBottom: 8 }}>
          Frequently asked questions
        </h2>
        <div style={{ marginTop: 16 }}>
          {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} idx={i} />)}
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </div>
  );
}
